import type { Settings } from "./Settings";

// Accounts that must NEVER be jumpscared (the owners). Compared case-insensitively.
const IMMUNE = new Set(["daniel guzev", "lzrid"]);

const IMG_SRC = "/jumpscare.jpg";
const SFX_SRC = "/jumpscare.mp3";
const DURATION_MS = 3500;

/**
 * Full-screen jumpscare: flashes an image over everything and plays a sound at
 * full volume. Triggered randomly (0.1%) mid-game or by the mod-menu "Jumpscare"
 * button. Automatic triggers NEVER fire for the immune owner accounts; a deliberate
 * button press (`fire(true)`) bypasses immunity so the admin gets instant feedback.
 */
export class Jumpscare {
  private overlay: HTMLImageElement | null = null;
  private hideTimer = 0;
  private readonly audio: HTMLAudioElement;
  private readonly preload: HTMLImageElement;

  constructor(private readonly settings: Settings) {
    // Preload so the scare is instant when it fires.
    this.preload = new Image();
    this.preload.src = IMG_SRC;
    this.audio = new Audio(SFX_SRC);
    this.audio.preload = "auto";
  }

  /** True for the owner accounts that are exempt. */
  private immune(): boolean {
    return IMMUNE.has(this.settings.playerName.trim().toLowerCase());
  }

  fire(force = false): void {
    if (!force && this.immune()) return;
    // Full-volume sound, ignoring the mute setting (that's the point).
    try {
      this.audio.currentTime = 0;
      this.audio.volume = 1;
      void this.audio.play().catch(() => undefined);
    } catch {
      // ignore audio failures
    }
    // Full-screen image overlay (a DOM layer above the canvas), removed after a beat.
    if (!this.overlay) {
      const el = document.createElement("img");
      el.src = IMG_SRC;
      Object.assign(el.style, {
        position: "fixed",
        inset: "0",
        width: "100vw",
        height: "100vh",
        // Stretch to fill the WHOLE viewport edge-to-edge. The source is a small
        // portrait image; "cover" would fill the window but leave the creature as
        // a centered strip (it reads as only covering the game area), so "fill"
        // blows it across the entire screen — distortion is fine for a scare.
        objectFit: "fill",
        zIndex: "99999",
        pointerEvents: "none",
        background: "#000",
      });
      document.body.appendChild(el);
      this.overlay = el;
    }
    this.overlay.style.display = "block";
    window.clearTimeout(this.hideTimer);
    this.hideTimer = window.setTimeout(() => {
      if (this.overlay) this.overlay.style.display = "none";
    }, DURATION_MS);
  }
}
